import React, { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'

interface EditPaymentMethodSheetProps {
  method: CrmFundraisingPaymentMethod | null
  onOpenChange: (open: boolean) => void
}

export function EditPaymentMethodSheet({
  method,
  onOpenChange,
}: EditPaymentMethodSheetProps) {
  const form = useForm({ payment_method: { name: '' } })
  const { setData, patch, processing, errors, reset } = form

  // Dep on id only: avoids re-seeding (and clobbering edits) when the parent
  // re-renders with the same record but a new object reference.
  useEffect(() => {
    if (method) {
      setData('payment_method', { name: method.name })
    }
  }, [method?.id])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!method) return
    patch(`/fundraising/payment_methods/${method.id}`, {
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={!!method} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Renommer le mode de paiement</SheetTitle>
          <SheetDescription>
            Seul le nom affiche change. Le code reste inchange.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="edit_pm_name">Nom *</Label>
            <Input
              id="edit_pm_name"
              value={form.data.payment_method.name}
              onChange={(e) =>
                setData('payment_method', { name: e.target.value })
              }
              required
              autoFocus
            />
            {/* The update interaction merges model errors as flat attribute
                keys (`errors.add(e.attribute, ...)`), so read `name` directly. */}
            {errors['name'] && (
              <p className="text-sm text-destructive">
                {errors['name']}
              </p>
            )}
          </div>

          {method && (
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={method.code ?? '-'} disabled className="font-mono text-xs" />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing} className="flex-1">
              {processing ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
